import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Checkbox from '@repo/ui/checkbox'
import { EuroIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../../lib/types/enums/mission/mission-type.enum'
import {
  MissionTypeAvailabilityEnum,
  missionTypeAvailabilityEnumMapping
} from '../../../lib/types/enums/mission/mission-type-availability.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { MissionTypeInput } from '../../../lib/types/models/mission-type/mission-type-input'
import { BaseFormProps, NullableTransformer } from '../../../lib/types/utils'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<NullableTransformer<MissionTypeInput>> = z.object({
  type: createEnumSchema({ fieldName: 'Catégorie', enumType: MissionTypeEnum }),
  title: createStringSchema('Libellé', { minimumLength: 1 }),
  qualifications: createEnumArraySchema({
    fieldName: 'qualification',
    enumType: StaffQualificationEnum
  }).nullish(),
  pay: createNumberSchema('Rémunération', { nullish: true }),
  requiredAvailability: createEnumSchema({
    fieldName: 'Disponibilité requise',
    enumType: MissionTypeAvailabilityEnum,
    nullish: true
  }),
  hotelRequired: z.boolean().optional(),
  ticketingRequired: z.boolean().optional(),
  convoyageMission: z.boolean().optional()
})

export const MissionTypeForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<MissionTypeInput>) => {
  const { t } = useTranslate()
  const form = useForm<MissionTypeInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const type = form.watch('type')

  return (
    <Form {...form}>
      <form>
        <InnerBox className={'p-4 rounded-2xl flex flex-col gap-3'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Type de mission
          </Typography>

          <FormField
            name={'type'}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    required
                    {...field}
                    label={'Catégorie'}
                    options={Object.entries(missionTypeEnumMapping).map(([key, value]) => ({
                      label: value.label,
                      value: key
                    }))}
                    selectedOption={
                      field.value
                        ? { label: missionTypeEnumMapping[field.value].label, value: field.value }
                        : undefined
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name={'title'}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput {...field} label={'Libellé'} type={'text'} required />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name={'qualifications'}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    searchable
                    variant="primary"
                    label="Qualifications requises"
                    options={Object.values(StaffQualificationEnum).map((qualification) => ({
                      label: t(staffQualificationEnumMapping[qualification].label),
                      value: qualification
                    }))}
                    selectedOptions={field.value?.map((qualification: StaffQualificationEnum) => ({
                      label: t(staffQualificationEnumMapping[qualification].label),
                      value: qualification
                    }))}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name={'pay'}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    label={'Rémunération'}
                    icon={EuroIcon}
                    type={'number'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            name={'requiredAvailability'}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    optional
                    label={'Disponibilité requise'}
                    options={Object.entries(missionTypeAvailabilityEnumMapping).map(
                      ([key, value]) => ({
                        label: value,
                        value: key
                      })
                    )}
                    selectedOption={conditionalRender(!!field?.value, {
                      label:
                        missionTypeAvailabilityEnumMapping[
                          field.value as keyof typeof missionTypeAvailabilityEnumMapping
                        ],
                      value: field.value
                    })}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <InnerBox className="w-fit">
            <FormField
              control={form.control}
              name={'convoyageMission'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Checkbox
                      {...field}
                      label={'Mission de convoyage'}
                      labelVariant="small"
                      checked={field.value}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </InnerBox>
          {type === MissionTypeEnum.STAFF && (
            <InnerBox className="w-fit flex flex-col gap-2">
              <FormField
                control={form.control}
                name={'ticketingRequired'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        {...field}
                        label={'Billetterie nécessaire'}
                        labelVariant="small"
                        checked={field.value}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'hotelRequired'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Checkbox
                        {...field}
                        label={'Hôtel nécessaire'}
                        labelVariant="small"
                        checked={field.value}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </InnerBox>
          )}
        </InnerBox>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}
